import React, { useState } from 'react';
import { UsersIcon, GraduationCapIcon, MoreHorizontalIcon, CheckIcon, EditIcon, CopyIcon, ArchiveIcon, EyeIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
interface ClassroomCardProps {
  id: string;
  name: string;
  grade: string;
  section?: string;
  learnerCount: number;
  educatorCount: number;
  status: 'active' | 'archived';
  type: 'organization' | 'private';
  academicYear: string;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  onRename?: (id: string, currentName: string) => void;
  onDuplicate?: (id: string) => void;
  onArchive?: (id: string) => void;
}
export const ClassroomCard: React.FC<ClassroomCardProps> = ({
  id,
  name,
  grade,
  section,
  learnerCount,
  educatorCount,
  status,
  type,
  academicYear,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
  onRename,
  onDuplicate,
  onArchive
}) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const handleClick = (e: React.MouseEvent) => {
    if (isSelectionMode && onToggleSelect) {
      e.preventDefault();
      onToggleSelect(id);
    } else {
      navigate(`/organization/classrooms/${id}`);
    }
  };
  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    navigate(`/organization/classrooms/${id}`);
  };
  const handleRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    if (onRename) {
      onRename(id, name);
    }
  };
  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    if (onDuplicate) {
      onDuplicate(id);
    }
  };
  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    if (onArchive) {
      onArchive(id);
    }
  };
  return <motion.div whileHover={!isSelectionMode ? {
    y: -2
  } : undefined} onClick={handleClick} className={`group bg-white rounded-xl border p-5 transition-all cursor-pointer relative overflow-hidden ${isSelected ? 'border-[#00B75F] ring-1 ring-[#00B75F] bg-[#00B75F]/5' : 'border-custom hover:border-[#00B75F]/50 hover:shadow-md'}`} data-id="element-938">
      {/* Selection Checkbox Overlay */}
      {isSelectionMode && <div className="absolute top-4 right-4 z-10" data-id="element-939">
          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-[#00B75F] border-[#00B75F]' : 'bg-white border-gray-300 group-hover:border-[#00B75F]'}`} data-id="element-940">
            {isSelected && <CheckIcon size={14} className="text-white" data-id="element-941" />}
          </div>
        </div>}

      {/* Status Stripe */}
      <div className={`absolute top-0 left-0 w-1 h-full ${status === 'active' ? 'bg-[#00B75F]' : 'bg-gray-300'}`} data-id="element-942" />

      <div className="flex justify-between items-start mb-3 pl-2" data-id="element-943">
        <div data-id="element-944">
          <div className="flex items-center gap-2 mb-1" data-id="element-945">
            <h3 className="text-lg font-bold text-text-primary group-hover:text-[#00B75F] transition-colors" data-id="element-946">
              {name}
            </h3>
            {status === 'archived' && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500 uppercase tracking-wide border border-gray-200" data-id="element-947">
                Archived
              </span>}
          </div>
          <p className="text-sm text-text-secondary" data-id="element-948">
            {grade} {section && `• Section ${section}`}
          </p>
        </div>

        {!isSelectionMode && <DropdownMenu.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen} data-id="element-949">
            <DropdownMenu.Trigger asChild data-id="element-950">
              <button onClick={e => e.stopPropagation()} className="p-1.5 rounded-lg text-text-tertiary hover:bg-gray-100 hover:text-text-primary transition-colors" aria-label="Classroom actions" data-id="element-951">
                <MoreHorizontalIcon size={18} data-id="element-952" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal data-id="element-953">
              <DropdownMenu.Content className="min-w-[200px] bg-surface-elevated rounded-xl border border-custom shadow-custom-lg p-1 z-50" sideOffset={5} align="end" onClick={e => e.stopPropagation()} data-id="element-954">
                <DropdownMenu.Item className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" onSelect={handleViewDetails} data-id="element-955">
                  <EyeIcon size={16} className="text-[#2563EB]" data-id="element-956" />
                  View Details
                </DropdownMenu.Item>

                {status === 'active' && <>
                    <DropdownMenu.Item className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" onSelect={handleRename} data-id="element-957">
                      <EditIcon size={16} className="text-text-secondary" data-id="element-958" />
                      Rename Classroom
                    </DropdownMenu.Item>

                    <DropdownMenu.Item className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" onSelect={handleDuplicate} data-id="element-959">
                      <CopyIcon size={16} className="text-text-secondary" data-id="element-960" />
                      Duplicate
                    </DropdownMenu.Item>

                    <DropdownMenu.Separator className="h-px bg-custom my-1" data-id="element-961" />

                    <DropdownMenu.Item className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 cursor-pointer outline-none" onSelect={handleArchive} data-id="element-962">
                      <ArchiveIcon size={16} data-id="element-963" />
                      Archive Classroom
                    </DropdownMenu.Item>
                  </>}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>}
      </div>

      <div className="flex items-center gap-4 pl-2 mt-4" data-id="element-964">
        <div className="flex items-center gap-1.5 text-sm text-text-secondary" data-id="element-965">
          <UsersIcon size={16} className="text-text-tertiary" data-id="element-966" />
          <span className="font-medium text-text-primary" data-id="element-967">{learnerCount}</span>
          <span className="text-xs" data-id="element-968">students</span>
        </div>

        <div className="flex items-center gap-1.5 text-sm text-text-secondary" data-id="element-969">
          <GraduationCapIcon size={16} className="text-text-tertiary" data-id="element-970" />
          <span className="font-medium text-text-primary" data-id="element-971">{educatorCount}</span>
          <span className="text-xs" data-id="element-972">educators</span>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center pl-2" data-id="element-973">
        <span className="text-xs text-text-tertiary font-medium" data-id="element-974">
          {academicYear}
        </span>

        {type === 'organization' ? <span className="text-[10px] font-bold text-[#2563EB] bg-[#2563EB]/10 px-2 py-1 rounded border border-[#2563EB]/20" data-id="element-975">
            ORG CLASSROOM
          </span> : <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded border border-gray-200" data-id="element-976">
            PRIVATE
          </span>}
      </div>
    </motion.div>;
};